import React, {
  useState,
  useEffect,
  useMemo,
  useCallback,
} from "react";
import { Flex } from "uikit-react";
import { useCookies } from "react-cookie";
import RecordsPerPage from "../../../common/RecordsPerPage";
import Pagination from "../../../common/Pagination";
import StorageStockAdjust from "./add";

const DEFAULT_RPP = 10;

const STATUS_FILTER_OPTIONS = [
  { label: "All Status", value: "" },
  { label: "OK", value: "ok" },
  { label: "Low", value: "low" },
  { label: "Out", value: "out" },
];

// Dummy locations (ids should line up with your locations page if you want)
const LOCATION_DUMMY = [
  { id: "loc-1", name: "Main Warehouse" },
  { id: "loc-2", name: "Driftwood Villa – Owner Closet" },
  { id: "loc-3", name: "Sand Dollar Linen Room" },
  { id: "loc-4", name: "Maintenance Garage" },
];

// Dummy items (ids should line up with /items page if you want)
const ITEM_DUMMY = [
  {
    id: "item-1",
    name: "Queen Sheet Set – White",
    sku: "QS-WHT",
  },
  {
    id: "item-2",
    name: "Bath Towel – White",
    sku: "BT-WHT",
  },
  {
    id: "item-3",
    name: "Dish Sponge – 3 Pack",
    sku: "SPG-3PK",
  },
  {
    id: "item-4",
    name: "Toilet Paper – 12 Pack",
    sku: "TP-12",
  },
];

// Dummy stock snapshot
const STOCK_DUMMY = [
  {
    id: "stk-1",
    location_id: "loc-1",
    location_name: "Main Warehouse",
    item_id: "item-1",
    item_name: "Queen Sheet Set – White",
    sku: "QS-WHT",
    on_hand: 42,
    reorder_point: 10,
  },
  {
    id: "stk-2",
    location_id: "loc-1",
    location_name: "Main Warehouse",
    item_id: "item-2",
    item_name: "Bath Towel – White",
    sku: "BT-WHT",
    on_hand: 18,
    reorder_point: 30,
  },
  {
    id: "stk-3",
    location_id: "loc-3",
    location_name: "Sand Dollar Linen Room",
    item_id: "item-2",
    item_name: "Bath Towel – White",
    sku: "BT-WHT",
    on_hand: 6,
    reorder_point: 10,
  },
  {
    id: "stk-4",
    location_id: "loc-4",
    location_name: "Maintenance Garage",
    item_id: "item-3",
    item_name: "Dish Sponge – 3 Pack",
    sku: "SPG-3PK",
    on_hand: 0,
    reorder_point: 5,
  },
];

const statusFromLevels = (row) => {
  const qty = Number(row.on_hand) || 0;
  const rp = row.reorder_point != null ? Number(row.reorder_point) : null;
  if (qty <= 0) return "out";
  if (rp != null && qty <= rp) return "low";
  return "ok";
};

const statusBadge = (row) => {
  const s = statusFromLevels(row);
  if (s === "out") return <span className="uk-badge uk-background-danger">Out</span>;
  if (s === "low") return <span className="uk-badge uk-background-warning">Low</span>;
  return <span className="uk-badge">OK</span>;
};

export default function StorageStockIndex() {
  const [cookies] = useCookies(["records-per-page"]);
  const recordsPerPage =
    Number(cookies["records-per-page"] || DEFAULT_RPP) || DEFAULT_RPP;

  const [stock, setStock] = useState(STOCK_DUMMY);
  const [criteria, setCriteria] = useState({
    search: "",
    location_id: "",
    status: "",
    page: 0,
  });
  const [editingEntry, setEditingEntry] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false);

  useEffect(() => {
    document.title = "Manage Stock - RueBaRue";
  }, []);

  const handleCriteriaChange = useCallback((key, value) => {
    setCriteria((prev) => ({
      ...prev,
      [key]: value,
      page:
        key === "search" || key === "location_id" || key === "status"
          ? 0
          : prev.page,
    }));
  }, []);

  const handleIndexChanged = useCallback((newPageIndex) => {
    setCriteria((prev) => ({ ...prev, page: newPageIndex }));
  }, []);

  const locationOptions = useMemo(() => LOCATION_DUMMY, []);
  const itemOptions = useMemo(() => ITEM_DUMMY, []);

  const filtered = useMemo(() => {
    return stock.filter((row) => {
      if (criteria.location_id && row.location_id !== criteria.location_id) {
        return false;
      }

      if (criteria.status) {
        const s = statusFromLevels(row);
        if (s !== criteria.status) return false;
      }

      if (criteria.search) {
        const q = criteria.search.toLowerCase();
        const hay = `${row.item_name || ""} ${row.sku || ""} ${
          row.location_name || ""
        }`.toLowerCase();
        if (!hay.includes(q)) return false;
      }

      return true;
    });
  }, [stock, criteria]);

  const totalPages = Math.max(
    1,
    Math.ceil(filtered.length / recordsPerPage)
  );
  const pageRows = filtered.slice(
    criteria.page * recordsPerPage,
    criteria.page * recordsPerPage + recordsPerPage
  );

  // Handle adjust/add stock
  const handleSaveStock = async (payload) => {
    // payload: { location_id, item_id, quantity, action, note }
    setIsProcessing(true);
    try {
      setStock((prev) => {
        const { location_id, item_id, quantity, action } = payload;
        const qty = Number(quantity) || 0;

        const existingIndex = prev.findIndex(
          (r) => r.location_id === location_id && r.item_id === item_id
        );

        const baseRow =
          existingIndex >= 0 ? prev[existingIndex] : (() => {
            const loc = LOCATION_DUMMY.find((l) => l.id === location_id) || {};
            const item = ITEM_DUMMY.find((i) => i.id === item_id) || {};
            return {
              id: `stk-${Date.now()}`,
              location_id,
              location_name: loc.name || "Unknown Location",
              item_id,
              item_name: item.name || "Unknown Item",
              sku: item.sku || "",
              on_hand: 0,
              reorder_point: null,
            };
          })();

        let nextQty = Number(baseRow.on_hand) || 0;

        if (action === "add") {
          nextQty = nextQty + qty;
        } else if (action === "remove") {
          nextQty = Math.max(0, nextQty - qty);
        } else if (action === "set") {
          nextQty = qty;
        }

        const updatedRow = { ...baseRow, on_hand: nextQty };

        if (existingIndex >= 0) {
          const copy = [...prev];
          copy[existingIndex] = updatedRow;
          return copy;
        } else {
          return [...prev, updatedRow];
        }
      });

      return true;
    } finally {
      setIsProcessing(false);
    }
  };

  const handleCancelAdjust = () => {
    setEditingEntry(null);
  };

  return (
    <>
      <section>
        <div id="hms-page-title">
          <h1 className="uk-heading-small">Manage Stock</h1>
        </div>

        <div id="hms-main-body">
          <div className="uk-flex uk-flex-between uk-flex-top">
            <button
              className="uk-button hms-btn hms-red-btn"
              uk-toggle="target: #modal-storage-stock-add"
              type="button"
              onClick={() => setEditingEntry(null)}
            >
              <span uk-icon="icon: plus; ratio: 0.7"></span> Adjust Stock
            </button>
          </div>

          <div className="uk-card uk-card-small uk-card-default hms-form-card card-without-filters">
            <div className="uk-overflow-auto">
              {/* Filters */}
              <Flex
                alignment="between top"
                className="guest-form-top-filters"
              >
                <form
                  className="uk-form uk-flex uk-flex-middle hms-form"
                  onSubmit={(e) => e.preventDefault()}
                >
                  {/* Search */}
                  <div className="uk-inline uk-margin-small-right">
                    <div className="uk-search uk-search-default uk-form-custom">
                      {criteria.search ? (
                        <a
                          className="uk-form-icon-flip uk-form-icon"
                          uk-icon="icon: close"
                          onClick={() =>
                            handleCriteriaChange("search", "")
                          }
                        ></a>
                      ) : (
                        <span
                          className="uk-search-icon-flip"
                          uk-search-icon="true"
                        />
                      )}
                      <input
                        className="uk-search-input"
                        type="search"
                        placeholder="Search item, SKU, or location"
                        value={criteria.search}
                        onChange={(e) =>
                          handleCriteriaChange("search", e.target.value)
                        }
                      />
                    </div>
                  </div>

                  {/* Location filter */}
                  <div
                    className="uk-inline uk-margin-small-right"
                    uk-form-custom="target: > * > span:first-child"
                  >
                    <select
                      value={criteria.location_id}
                      onChange={(e) =>
                        handleCriteriaChange("location_id", e.target.value)
                      }
                    >
                      <option value="">All Locations</option>
                      {locationOptions.map((loc) => (
                        <option key={loc.id} value={loc.id}>
                          {loc.name}
                        </option>
                      ))}
                    </select>
                    <button
                      className="uk-button uk-button-default"
                      type="button"
                      tabIndex="-1"
                    >
                      <span className="select-value">
                        {criteria.location_id
                          ? locationOptions.find(
                              (l) => l.id === criteria.location_id
                            )?.name || "All Locations"
                          : "All Locations"}
                      </span>
                      <span
                        uk-icon="icon: chevron-down"
                        className="hms-align-right"
                      ></span>
                    </button>
                  </div>

                  {/* Status filter */}
                  <div
                    className="uk-inline uk-margin-small-right"
                    uk-form-custom="target: > * > span:first-child"
                  >
                    <select
                      value={criteria.status}
                      onChange={(e) =>
                        handleCriteriaChange("status", e.target.value)
                      }
                    >
                      {STATUS_FILTER_OPTIONS.map((opt) => (
                        <option key={opt.value} value={opt.value}>
                          {opt.label}
                        </option>
                      ))}
                    </select>
                    <button
                      className="uk-button uk-button-default"
                      type="button"
                      tabIndex="-1"
                    >
                      <span className="select-value">
                        {criteria.status
                          ? STATUS_FILTER_OPTIONS.find(
                              (o) => o.value === criteria.status
                            )?.label
                          : "All Status"}
                      </span>
                      <span
                        uk-icon="icon: chevron-down"
                        className="hms-align-right"
                      ></span>
                    </button>
                  </div>
                </form>
              </Flex>

              {/* Table */}
              <table className="uk-table uk-table-hover uk-table-divider hms-table">
                <thead>
                  <tr>
                    <th className="uk-table-expand">Location</th>
                    <th className="uk-table-expand">Item</th>
                    <th>SKU</th>
                    <th>On Hand</th>
                    <th>Reorder Point</th>
                    <th>Status</th>
                    <th className="actions">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {pageRows.length === 0 && (
                    <tr>
                      <td colSpan={7}>
                        <div className="uk-text-center uk-padding-small uk-text-meta">
                          No stock found. Adjust your filters.
                        </div>
                      </td>
                    </tr>
                  )}

                  {pageRows.map((row) => (
                    <tr key={row.id}>
                      <td className="semi-bold-font">
                        {row.location_name || "—"}
                      </td>
                      <td>{row.item_name || "—"}</td>
                      <td>{row.sku || "—"}</td>
                      <td>{row.on_hand ?? 0}</td>
                      <td>{row.reorder_point ?? "—"}</td>
                      <td>{statusBadge(row)}</td>
                      <td className="hms-action-btn">
                        <div className="uk-inline">
                          <button
                            className="uk-button"
                            type="button"
                            uk-toggle="target: #modal-storage-stock-add"
                            uk-tooltip="Adjust Stock"
                            onClick={() => setEditingEntry(row)}
                          >
                            <img src="/images/edit.svg" alt="Adjust" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>

              <Flex
                alignment="between top"
                className="guest-form-top-filters"
              >
                <Pagination
                  count={totalPages}
                  index={Math.min(criteria.page, totalPages - 1)}
                  onIndexChanged={handleIndexChanged}
                />
                <RecordsPerPage />
              </Flex>
            </div>
          </div>
        </div>
      </section>

      {/* Adjust / Add Stock modal */}
      <StorageStockAdjust
        entry={editingEntry}
        locations={locationOptions}
        items={itemOptions}
        flagIsProcessing={isProcessing}
        onSave={handleSaveStock}
        onCancel={handleCancelAdjust}
      />
    </>
  );
}